import { tw } from "@careinfo/tailwind";
import {
  ColumnDef,
  getCoreRowModel,
  getPaginationRowModel,
  Row,
  useReactTable,
} from "@tanstack/react-table";
import { useEffect, useState } from "react";
import { useAsyncError, useSearchParams } from "react-router-dom";
import APIS from "../../../constant/Endpoint";
import { useDebounce } from "../../../customHooks/useDebounceHook";
import useGetHook from "../../../customHooks/useGetHook";
import Template, { TableBodyLoading } from "../Datatable/loading";
import TableSearch from "../Datatable/components/Search";
import TableHead from "../Datatable/components/Head";
import TablePagination from "../Datatable/components/Navigation";
import TableBody from "./TableBody";

interface IDataTable {
  url: any;
  columns: ColumnDef<any, any>[];
  params?: Record<string, any>;
  queryKey: string;
  limit?: number;
  handleRowClick?: (row: Row<any>) => void;
  customSearch?: boolean;
  addNew?: {
    path: string;
    label: string;
  };
}

/**
 * Used to display the array of data
 * @param {IDataTable} props - props of data table
 *
 * @property `addNew`  can be added to show a button to redirect to
 * add entry `{ path:  string, label: string }`
 * @property `customSearch` can be add to place a search element in document and
 * the search should add `query parameter named search` for searching datatable to work.
 * @property `columns` pass column definition as describe in Tanstack Table `ColumDef` as an array
 * @property `url` URL must be defined in `project_source/endpoints/constanst.ts` as either string
 * or value from object
 * @property
 */
export default function GeneralLedgerTable({
  url,
  queryKey,
  columns,
  addNew = null,
  customSearch = false,
  params,
  limit: upperLimit = 10,
  handleRowClick = () => {},
}: IDataTable) {
  const [searchParams, _] = useSearchParams({
    limit: "10",
    page: "0",
    searchQuery: "",
  });

  const page = parseInt(searchParams.get("page") ?? "0");
  const searchQuery = searchParams.get("search");
  const debouncesearch = useDebounce(searchQuery);
  const [limit, setLimit] = useState(upperLimit);
  const [data, setData] = useState([]);
  const [closingData, setClosingData] = useState(null);

  const {
    data: serverData,
    isLoading,
    isRefetching,
  } = useGetHook({
    queryKey: [
      queryKey,
      `${queryKey}-${limit}-${page}-${JSON.stringify(
        params
      )}-${debouncesearch}`,
    ],
    url: url,
    params: {
      limit,
      offset: limit * (isNaN(page) ? 0 : page),
      search: debouncesearch,
      ...params,
    },
  });

  const table = useReactTable({
    data,
    columns,
    enableRowSelection: true,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
  });

  useEffect(() => {
    if (Array.isArray(serverData?.results)) {
      const rawData = serverData.results ?? [];
      const totalCredit = rawData.reduce(
        (sum, item) => sum + (parseFloat(item.credit) || 0),
        0
      );
      const totalDebit = rawData.reduce(
        (sum, item) => sum + (parseFloat(item.debit) || 0),
        0
      );
      const closingBalance = totalCredit - totalDebit;

      const closingRow = {
        id: "closing-balance",
        particular: "Closing",
        credit: totalCredit.toFixed(2),
        debit: totalDebit.toFixed(2),
        balance: closingBalance.toFixed(2),
        is_draft: false,
        type: "",
        created: null,
        isTotalRow: true,
      };
      setClosingData(closingRow);
      setData([...rawData]);
    }
  }, [serverData]);

  useEffect(() => {
    table.setPageSize(limit);
  }, [limit, table]);

  return isLoading ? (
    <Template
      addNew={addNew}
      customSearch={!customSearch}
      noOfColumns={columns.length}
    />
  ) : (
    <>
      <div className={tw`w-full rounded-sm`}>
        {!customSearch && (
          <div
            className={tw`w-full grid grid-cols-1 gap-4 md:flex justify-between items-center py-4`}
          >
            <TableSearch addNew={addNew} />
          </div>
        )}
        <div
          className={tw`bg-[#fefefe] rounded-md border border-[#dcdcdc]`}
          style={{ boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)" }}
        >
          <div className={tw`w-full overflow-auto z-0 rounded-t-md`}>
            <table className={tw`w-full`}>
              <TableHead table={table} />

              {isRefetching ? (
                <TableBodyLoading noOfColumns={columns.length} />
              ) : !data?.length ? (
                <tr>
                  <td
                    colSpan={columns.length}
                    className={tw`text-[#949494] text-2xl leading-8 text-center py-10`}
                  >
                    No entries found.
                  </td>
                </tr>
              ) : (
                <>
                  <TableBody table={table} handleRowClick={handleRowClick} />
                  <tr
                    className={tw`font-semibold text-primary h-8 bg-gray-200`}
                  >
                    <td colSpan={3} className={tw`px-4`}>
                      {closingData?.particular}
                    </td>
                    <td className={tw`px-4`}>
                      {Number(closingData?.debit).toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </td>
                    <td className={tw`px-4`}>
                      {Number(closingData?.credit).toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </td>
                    <td colSpan={2} className={tw`px-4`}>
                      {Math.abs(closingData?.balance).toLocaleString("en-IN", {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                      {closingData?.balance < 0 ? "DR" : "CR"}
                    </td>
                  </tr>
                </>
              )}
            </table>
          </div>
          <TablePagination
            serverData={serverData}
            limit={limit}
            table={table}
            setLimit={setLimit}
          />
        </div>
      </div>
    </>
  );
}
